import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import BackupRestore from '../../utils/backupRestore';
import { testFirestoreWrite } from '../../utils/testFirestore';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Backup,
  Restore,
  Save,
  MoreVert,
  History,
  Download,
  Upload,
  CloudDownload,
  Delete,
  Settings
} from '@mui/icons-material';

export default function BackupsRestore() {
  const { userProfile, currentUser } = useAuth();
  const [backups, setBackups] = useState([]);
  const [restorePoints, setRestorePoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openBackupDialog, setOpenBackupDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [openRestorePointDialog, setOpenRestorePointDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [backupName, setBackupName] = useState('');
  const [restorePointName, setRestorePointName] = useState('');
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [selectedRestorePoint, setSelectedRestorePoint] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');
  const [menuId, setMenuId] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [maxBackups, setMaxBackups] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [backupsData, restorePointsData] = await Promise.all([
        BackupRestore.getBackups(),
        BackupRestore.getRestorePoints()
      ]);
      setBackups(backupsData);
      setRestorePoints(restorePointsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setLoading(true);
      
      // First test Firestore write permissions
      console.log('Testing Firestore write permissions...');
      const testResult = await testFirestoreWrite();
      if (!testResult.success) {
        alert('Firestore write test failed: ' + testResult.error);
        return;
      }
      console.log('Firestore write test passed');
      
      await BackupRestore.createBackup(
        currentUser.uid,
        userProfile.email,
        backupName || undefined,
        maxBackups
      );
      setBackupName('');
      setOpenBackupDialog(false);
      await fetchData();
      alert('Backup created successfully!');
    } catch (error) {
      console.error('Error creating backup:', error);
      alert('Error creating backup: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreFromBackup = async () => {
    if (!selectedBackup) return;
    
    if (window.confirm(`Are you sure you want to restore from backup "${selectedBackup.name}"? This will overwrite all current data.`)) {
      try {
        setLoading(true);
        await BackupRestore.restoreFromBackup(
          selectedBackup.id,
          currentUser.uid,
          userProfile.email
        );
        setOpenRestoreDialog(false);
        await fetchData();
        alert('Restore completed successfully!');
      } catch (error) {
        console.error('Error restoring backup:', error);
        alert('Error restoring backup: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCreateRestorePoint = async () => {
    try {
      setLoading(true);
      await BackupRestore.createRestorePoint(
        restorePointName,
        currentUser.uid,
        userProfile.email
      );
      setRestorePointName('');
      setOpenRestorePointDialog(false);
      await fetchData();
      alert('Restore point created successfully!');
    } catch (error) {
      console.error('Error creating restore point:', error);
      alert('Error creating restore point: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreToPoint = async () => {
    if (!selectedRestorePoint) return;
    
    if (window.confirm(`Are you sure you want to restore to point "${selectedRestorePoint.name}"? This will overwrite all current data.`)) {
      try {
        setLoading(true);
        await BackupRestore.restoreToPoint(
          selectedRestorePoint.id,
          currentUser.uid,
          userProfile.email
        );
        await fetchData();
        alert('Restore to point completed successfully!');
      } catch (error) {
        console.error('Error restoring to point:', error);
        alert('Error restoring to point: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDownloadBackup = async (backupId) => {
    try {
      setLoading(true);
      await BackupRestore.downloadBackup(backupId);
      alert('Backup downloaded successfully!');
    } catch (error) {
      console.error('Error downloading backup:', error);
      alert('Error downloading backup: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAndDownloadBackup = async () => {
    try {
      setLoading(true);
      await BackupRestore.createAndDownloadBackup(
        currentUser.uid,
        userProfile.email,
        backupName || undefined
      );
      setBackupName('');
      setOpenBackupDialog(false);
      alert('Backup created and downloaded successfully!');
    } catch (error) {
      console.error('Error creating and downloading backup:', error);
      alert('Error creating backup: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const fileContent = JSON.parse(e.target.result);
        setUploadedFile(fileContent);
        setUploadError('');
        
        // Show file info
        console.log('File uploaded:', fileContent.name);
        console.log('Collections:', fileContent.collections);
        console.log('Record counts:', fileContent.recordCounts);
      } catch (error) {
        console.error('Error parsing file:', error);
        setUploadError('Invalid backup file. Please upload a valid JSON backup file.');
        setUploadedFile(null);
      }
    };
    reader.readAsText(file);
  };

  const handleRestoreFromFile = async () => {
    if (!uploadedFile) return;
    
    if (window.confirm(`Are you sure you want to restore from "${uploadedFile.name}"? This will overwrite all current data.`)) {
      try {
        setLoading(true);
        const result = await BackupRestore.restoreFromFile(
          uploadedFile,
          currentUser.uid,
          userProfile.email
        );
        setOpenUploadDialog(false);
        setUploadedFile(null);
        await fetchData();
        alert(`Restore completed successfully! Restored ${result.restoredCount} records.`);
      } catch (error) {
        console.error('Error restoring from file:', error);
        alert('Error restoring from file: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMenuClick = (event, type, id) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
    setMenuId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuType('');
    setMenuId('');
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp.seconds * 1000).toLocaleString();
  };

  const handleDeleteBackup = async (backupId) => {
    if (window.confirm('Are you sure you want to delete this backup? This action cannot be undone.')) {
      try {
        setLoading(true);
        await BackupRestore.deleteBackup(backupId);
        await fetchData();
        alert('Backup deleted successfully!');
      } catch (error) {
        console.error('Error deleting backup:', error);
        alert('Error deleting backup: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteRestorePoint = async (restorePointId) => {
    if (window.confirm('Are you sure you want to delete this restore point? This action cannot be undone.')) {
      try {
        setLoading(true);
        await BackupRestore.deleteRestorePoint(restorePointId);
        await fetchData();
        alert('Restore point deleted successfully!');
      } catch (error) {
        console.error('Error deleting restore point:', error);
        alert('Error deleting restore point: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCleanupOldBackups = async () => {
    if (window.confirm(`This will keep only the ${maxBackups} most recent backups and delete all older ones. Continue?`)) {
      try {
        setLoading(true);
        const deletedCount = await BackupRestore.cleanupOldBackups(maxBackups);
        await fetchData();
        alert(`Cleanup complete! Deleted ${deletedCount} old backup(s).`);
        setOpenSettingsDialog(false);
      } catch (error) {
        console.error('Error cleaning up backups:', error);
        alert('Error cleaning up backups: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return <Container><Typography>Loading backups...</Typography></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Backups and Restore</Typography>
        <Button
          variant="outlined"
          startIcon={<Settings />}
          onClick={() => setOpenSettingsDialog(true)}
        >
          Backup Settings
        </Button>
      </Box>

      {/* Settings Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Automatic cleanup is enabled. Only the {maxBackups} most recent backups will be kept.
      </Alert>
      
      {/* Backup Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Database Backups ({backups.length})</Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setOpenUploadDialog(true)}
            >
              Upload & Restore
            </Button>
            <Button
              variant="contained"
              startIcon={<Backup />}
              onClick={() => setOpenBackupDialog(true)}
            >
              Create Backup
            </Button>
          </Box>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Records</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.map((backup) => (
                <TableRow key={backup.id}>
                  <TableCell>{backup.name}</TableCell>
                  <TableCell>
                    {backup.timestamp ? formatTimestamp(backup.timestamp) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {Object.entries(backup.recordCounts || {}).map(([key, count]) => (
                      <Chip key={key} label={`${key}: ${count}`} size="small" sx={{ mr: 1 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => handleMenuClick(e, 'backup', backup.id)}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Restore Points Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Restore Points</Typography>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={() => setOpenRestorePointDialog(true)}
          >
            Create Restore Point
          </Button>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {restorePoints.map((point) => (
                <TableRow key={point.id}>
                  <TableCell>{point.name}</TableCell>
                  <TableCell>
                    {point.timestamp ? formatTimestamp(point.timestamp) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => handleMenuClick(e, 'restorePoint', point.id)}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Backup Dialog */}
      <Dialog open={openBackupDialog} onClose={() => setOpenBackupDialog(false)}>
        <DialogTitle>Create Backup</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Backup Name (Optional)"
            fullWidth
            variant="outlined"
            value={backupName}
            onChange={(e) => setBackupName(e.target.value)}
            placeholder="Leave empty for auto-generated name"
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Choose to save the backup to the database or download it as a file.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBackupDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateAndDownloadBackup} 
            variant="outlined"
            startIcon={<Download />}
          >
            Download as File
          </Button>
          <Button onClick={handleCreateBackup} variant="contained">
            Save to Database
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={openRestoreDialog} onClose={() => setOpenRestoreDialog(false)}>
        <DialogTitle>Restore from Backup</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will overwrite all current data with the selected backup.
          </Alert>
          <TextField
            select
            fullWidth
            label="Select Backup"
            value={selectedBackup?.id || ''}
            onChange={(e) => {
              const backup = backups.find(b => b.id === e.target.value);
              setSelectedBackup(backup);
            }}
          >
            {backups.map((backup) => (
              <MenuItem key={backup.id} value={backup.id}>
                {backup.name} - {backup.timestamp ? formatTimestamp(backup.timestamp) : 'N/A'}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRestoreDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleRestoreFromBackup} 
            variant="contained"
            disabled={!selectedBackup}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Restore Point Dialog */}
      <Dialog open={openRestorePointDialog} onClose={() => setOpenRestorePointDialog(false)}>
        <DialogTitle>Create Restore Point</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Restore Point Name"
            fullWidth
            variant="outlined"
            value={restorePointName}
            onChange={(e) => setRestorePointName(e.target.value)}
            required
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            This will create a restore point that you can return to at any time.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRestorePointDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateRestorePoint} 
            variant="contained"
            disabled={!restorePointName.trim()}
          >
            Create Restore Point
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload & Restore Dialog */}
      <Dialog open={openUploadDialog} onClose={() => setOpenUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload & Restore from File</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will overwrite all current data with the uploaded backup file.
          </Alert>
          
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<CloudDownload />}
            sx={{ mb: 2 }}
          >
            Select Backup File (JSON)
            <input
              type="file"
              accept=".json"
              hidden
              onChange={handleFileUpload}
            />
          </Button>

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          {uploadedFile && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Backup Details:</strong>
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {uploadedFile.name}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {new Date(uploadedFile.timestamp).toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Collections:</strong> {uploadedFile.collections?.join(', ')}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Record Counts:</strong>
                </Typography>
                {Object.entries(uploadedFile.recordCounts || {}).map(([key, count]) => (
                  <Chip key={key} label={`${key}: ${count}`} size="small" sx={{ mr: 1, mt: 0.5 }} />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenUploadDialog(false);
            setUploadedFile(null);
            setUploadError('');
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleRestoreFromFile} 
            variant="contained"
            color="primary"
            disabled={!uploadedFile}
            startIcon={<Restore />}
          >
            Restore from File
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backup Settings Dialog */}
      <Dialog open={openSettingsDialog} onClose={() => setOpenSettingsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Backup Settings</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Configure how many backups to keep. Older backups will be automatically deleted when new backups are created.
          </Alert>
          
          <TextField
            autoFocus
            margin="dense"
            label="Maximum Number of Backups"
            type="number"
            fullWidth
            variant="outlined"
            value={maxBackups}
            onChange={(e) => setMaxBackups(parseInt(e.target.value) || 10)}
            inputProps={{ min: 1, max: 100 }}
            helperText="Set to a higher number to keep more backup history"
          />

          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Current Status:</strong>
            </Typography>
            <Typography variant="body2">
              Total backups: {backups.length}
            </Typography>
            <Typography variant="body2">
              Will keep: {maxBackups} most recent
            </Typography>
            {backups.length > maxBackups && (
              <Typography variant="body2" color="warning.main">
                {backups.length - maxBackups} backup(s) will be deleted on cleanup
              </Typography>
            )}
          </Box>

          {backups.length > maxBackups && (
            <Button
              variant="outlined"
              color="warning"
              fullWidth
              sx={{ mt: 2 }}
              onClick={handleCleanupOldBackups}
              startIcon={<Delete />}
            >
              Clean Up Old Backups Now
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSettingsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {menuType === 'backup' && (
          <>
            <MenuItem onClick={() => {
              handleDownloadBackup(menuId);
              handleMenuClose();
            }}>
              <ListItemIcon><Download /></ListItemIcon>
              <ListItemText>Download backup file</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              setSelectedBackup(backups.find(b => b.id === menuId));
              setOpenRestoreDialog(true);
              handleMenuClose();
            }}>
              <ListItemIcon><Restore /></ListItemIcon>
              <ListItemText>Restore from this backup</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              handleDeleteBackup(menuId);
              handleMenuClose();
            }}>
              <ListItemIcon><Delete /></ListItemIcon>
              <ListItemText>Delete backup</ListItemText>
            </MenuItem>
          </>
        )}
        {menuType === 'restorePoint' && (
          <>
            <MenuItem onClick={() => {
              setSelectedRestorePoint(restorePoints.find(rp => rp.id === menuId));
              handleRestoreToPoint();
              handleMenuClose();
            }}>
              <ListItemIcon><History /></ListItemIcon>
              <ListItemText>Restore to this point</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              handleDeleteRestorePoint(menuId);
              handleMenuClose();
            }}>
              <ListItemIcon><Delete /></ListItemIcon>
              <ListItemText>Delete restore point</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Container>
  );
}

